import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ChatMessage, ChatResponse } from '../../types';
import { apiService } from '../../services/api';

interface ChatState {
  messages: ChatMessage[];
  suggestedQuestions: string[];
  chatBrief: string | null;
  pendingPrompt: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: ChatState = {
  messages: [],
  suggestedQuestions: [],
  chatBrief: null,
  pendingPrompt: null,
  isLoading: false,
  error: null,
};

interface SendMessagePayload {
  message: string;
  context?: string;
  pendingScenarioId?: string;
}


export const sendChatMessage = createAsyncThunk<
  ChatResponse,
  SendMessagePayload,
  { rejectValue: string }
>(
  'chat/sendMessage',
  async ({ message, context, pendingScenarioId }, { rejectWithValue }) => {
    try {
      const response = await apiService.sendChatMessage(message, context, pendingScenarioId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to send message');
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessage(state, action: PayloadAction<ChatMessage>) {
      state.messages.push(action.payload);
    },
    
    setPendingPrompt(state, action: PayloadAction<string | null>) {
      state.pendingPrompt = action.payload;
    },
    
    clearChat(state) {
      state.messages = [];
      state.suggestedQuestions = [];
      state.chatBrief = null;
      state.pendingPrompt = null;
      state.error = null;
    },
    
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendChatMessage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pendingPrompt = null;
        state.suggestedQuestions = action.payload.suggested_questions || [];
        state.chatBrief = action.payload.chat_brief || null;
        
        if (action.payload.response?.trim()) {
          state.messages.push({
            role: 'assistant',
            content: action.payload.response,
            timestamp: new Date().toISOString(),
          });
        }
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.pendingPrompt = null;
        state.error = action.payload || 'An error occurred';
        
        state.messages.push({
          role: 'assistant',
          content: `Error: ${action.payload || 'An error occurred'}`,
          timestamp: new Date().toISOString(),
        });
      });
  },
});

export const { addMessage, setPendingPrompt, clearChat, clearError } = chatSlice.actions;
export default chatSlice.reducer;
